import { Card } from '@ksyos/design-system';
import { useEffect, useState } from 'react';
import type { IKsyosProfileDetails } from './addKsyosEmployeeState';
import { Gender } from '../../../../../common/enums/gender';
import { ksyosProfileDetailsSchema } from '../../../../../common/validation/practitioner';
import { translateKey } from '../../../../translate';
import { useValidationErrors } from '../../../../utils/hooks/useValidationErrors';
import { updateValidationErrorsForOne, validate } from '../../../../utils/validation';
import { FormContextProvider } from '../../../elements/formContext';
import { InputRadioGroup } from '../../../userDataManagement/InputRadioGroup';
import { InputTextField } from '../../../userDataManagement/InputText';

interface IProfileDetailsProps {
    onUpdateIsValid: (isValid: boolean) => void;
    onUpdateProfileDetails: (profileDetails: IKsyosProfileDetails) => void;
    profileDetails?: IKsyosProfileDetails;
}

export function ProfileDetails(props: IProfileDetailsProps) {
    const [profileDetails, setProfileDetails] = useState<IKsyosProfileDetails>(
        props.profileDetails ?? getEmptyProfileDetails()
    );
    const [errors, setErrors] = useValidationErrors();

    useEffect(() => {
        const { hasErrors } = validate(ksyosProfileDetailsSchema, profileDetails);

        props.onUpdateProfileDetails(profileDetails);
        props.onUpdateIsValid(!hasErrors);
    }, [profileDetails]);

    return (
        <FormContextProvider
            value={{
                placeholderGroupName: 'profileDetails',
                item: profileDetails,
                errors,
                onChange: async (key, value) => handleChange(key, value),
                onDoValidate: async (key) => validateOne(key),
            }}
        >
            <Card>
                <Card.Header title={translateKey('profile.generalSettings')} />
                <Card.Body>
                    {renderTextInput('common.nameGiven', 'nameGiven')}
                    {renderTextInput('common.nameInfix2', 'nameInfix')}
                    {renderTextInput('common.nameFamily', 'nameFamily', true)}

                    <InputRadioGroup
                        fieldKey="gender"
                        isRequired
                        label="common.gender"
                        radioItems={Object.keys(Gender).map((gender) => ({
                            id: gender,
                            label: translateKey(`genderEnum.${gender}`),
                            value: gender,
                        }))}
                    />

                    {renderTextInput('common.emailAddress', 'emailAddress', true)}
                    {renderTextInput('common.mobileTelephoneNumber', 'mobileNumber')}
                </Card.Body>
            </Card>
        </FormContextProvider>
    );

    function getEmptyProfileDetails() {
        return {
            nameGiven: null,
            nameInfix: null,
            nameFamily: '',
            gender: undefined,
            emailAddress: '',
            mobileNumber: null,
        };
    }

    function renderTextInput(labelKey: string, fieldKey: keyof IKsyosProfileDetails, isRequired: boolean = false) {
        return (
            <InputTextField
                error={errors.get(fieldKey)}
                fieldKey={fieldKey}
                hasErrors={errors.has(fieldKey)}
                isRequired={isRequired}
                label={labelKey}
                nullable={!isRequired}
                placeholder={`${translateKey('common.enterValuePlaceholder', translateKey(labelKey))}`}
            />
        );
    }

    function handleChange<K extends keyof IKsyosProfileDetails>(name: K, value: IKsyosProfileDetails[K]) {
        setProfileDetails((currentProfileDetails) => ({ ...currentProfileDetails, [name]: value }));
    }

    function validateOne<K extends keyof IKsyosProfileDetails>(name: K) {
        setErrors((currentErrors) =>
            updateValidationErrorsForOne(ksyosProfileDetailsSchema, profileDetails, currentErrors, name)
        );
    }
}
